import strings from 'utils/strings'
import { take, race, call, put, select } from 'redux-saga/effects'
import { equals, isEmpty, prop, path } from 'ramda'
import {
  CTPM_START_WORKFLOW,
  CTPM_PREP_DATA,
  CTPM_FINISHED_WORKFLOW,
  CTPM_SHOW_CTP_MAPPING_MODAL,
  CTPM_SELECT_PIN_TO_MAP,
  CPTM_SET_ASSIGNMENTS,
  CPTM_STAGE_ASSIGNMENTS,
  CTPM_CANCEL_CTP_MAPPING,
  CTPM_CLEAN_UP,
  CTPM_SAVE_CTP_MAPPING,
  CTPM_CLEAN_UP_ON_SAVE,
  CTPM_SELECT_PIN_TO_UNMAP,
} from 'actions/ctpMappingWorkflow'
import {
  getCtpAssignments,
  getCtpStagedAssignments,
  getAssignmentsMergedWithStage,
} from 'selectors/ctpMappingWorkflow'
import { getAvailableCircuits } from 'selectors/ctp'

export default function* watchCtpMappingWorkflow() {
  while (true) {
    const {
      payload: { serialNumber, circuits, selectedRPs, assignmentsToStage = [] },
    } = yield take(CTPM_START_WORKFLOW)

    const assignments = yield select(getCtpAssignments)

    const availableCircuits = isEmpty(circuits)
      ? yield select(getAvailableCircuits)
      : circuits

    const currentAssignmentsLessStaged = assignments.filter((curr) => {
      const uri = path(['pin', 'uri'], curr)
      return !assignmentsToStage.some((assignment) =>
        equals(uri, path(['pin', 'uri'], assignment))
      )
    })

    yield put({
      type: CTPM_PREP_DATA,
      payload: {
        serialNumber,
        circuits: availableCircuits,
        selectedRPs,
        assignments: currentAssignmentsLessStaged,
        stagedAssignments: assignmentsToStage,
      },
    })

    yield put({
      type: CTPM_SHOW_CTP_MAPPING_MODAL,
      payload: { visible: true },
    })

    yield race({
      events: call(watchEvents),
      finished: take(CTPM_FINISHED_WORKFLOW),
    })
  }
}

function* watchEvents() {
  while (true) {
    yield race({
      [CTPM_SELECT_PIN_TO_MAP]: call(watchSelectPinToMap),
      [CTPM_CANCEL_CTP_MAPPING]: call(watchCancelMapping),
      [CTPM_SAVE_CTP_MAPPING]: call(watchSaveMapping),
      [CTPM_SELECT_PIN_TO_UNMAP]: call(watchSelectPinToUnMap),
    })
  }
}

function* watchSaveMapping() {
  yield take(CTPM_SAVE_CTP_MAPPING)

  const assignments = yield select(getAssignmentsMergedWithStage)

  yield put({ type: CPTM_SET_ASSIGNMENTS, payload: { assignments } })
  yield put({
    type: CPTM_STAGE_ASSIGNMENTS,
    payload: { stagedAssignments: [] },
  })
  yield put({ type: CTPM_CLEAN_UP_ON_SAVE })
  yield put({ type: CTPM_FINISHED_WORKFLOW })

  return CTPM_SAVE_CTP_MAPPING
}

function* watchCancelMapping() {
  const { payload } = yield take(CTPM_CANCEL_CTP_MAPPING)
  const currentAssignments = yield select(getCtpAssignments)
  yield put({
    type: CTPM_CLEAN_UP,
    payload: {
      assignments: prop('flush', payload) ? [] : currentAssignments,
    },
  })
  yield put({ type: CTPM_FINISHED_WORKFLOW, payload: { canceled: true } })
  return CTPM_CANCEL_CTP_MAPPING
}

function* watchSelectPinToUnMap() {
  const {
    payload: { circuitNumber },
  } = yield take(CTPM_SELECT_PIN_TO_UNMAP)
  const stagedAssignments = yield select(getCtpStagedAssignments)
  const otherStagedAssignments = stagedAssignments.filter(
    (ass) => prop('circuitNumber', ass) !== circuitNumber
  )

  yield put({
    type: CPTM_STAGE_ASSIGNMENTS,
    payload: {
      stagedAssignments: [...otherStagedAssignments],
    },
  })
}

function* watchSelectPinToMap() {
  const {
    payload: { pin, circuitNumber, linkedRpUri },
  } = yield take(CTPM_SELECT_PIN_TO_MAP)
  const stagedAssignments = yield select(getCtpStagedAssignments)
  const otherStagedAssignments = stagedAssignments.filter(
    (ass) => prop('circuitNumber', ass) !== circuitNumber
  )

  yield put({
    type: CPTM_STAGE_ASSIGNMENTS,
    payload: {
      stagedAssignments: [
        ...otherStagedAssignments,
        {
          module: prop('moduleName', pin),
          connector: prop('connectorName', pin),
          pin,
          circuitNumber,
          linkedRpUri,
          mappingStatus: strings.pending,
        },
      ],
    },
  })
}
